// The container: URL in, active context out, shell around a page outlet.
//
// Routing is a plain read of location.pathname against the app's routes — there
// are few enough that a router dependency would be ceremony. The routes come from
// the page contracts:
//   /                      vault/Apps/Forge/Pages/Home.md — E14-t2. Until that
//                          ticket `/` parsed to `unknown`, so the app's own root
//                          was a "there is no such page" sentence.
//   /:org/:kb              vault/Apps/Forge/Pages/Home.md f4 — E14-t4. THE KB
//                          ITSELF: its primary format's published dashboard,
//                          resolved from the render manifest. See the note on
//                          `/:org/settings` below — that route is checked first
//                          and shadows this one for a KB named `settings`.
//   /:org/:kb/dashboards   vault/Apps/Forge/Pages/Dashboards.md
//   /:org/:kb/agent        vault/Apps/Forge/Pages/Agent Chat.md
//   /:org/settings         vault/Apps/Forge/Pages/Admin.md
//   /signin                E12-t29 — the ONE anonymous page (git-host/src/login.ts)
//   /auth/device           E12-t30 — the device-code `verification_uri` the CLI
//                          prints (git-host/src/auth.ts VERIFICATION_PATH)
// No route exists for Browser, History, Change Review or Work (Forge q2), so a
// hand-typed url for one of them lands on `unknown`, not on a hidden page.
//
// E14-t5 — THE AGENT SURFACES ARE HIDDEN, NOT RETIRED. `/:org/:kb/agent` still
// parses and still renders below, and this file still carries the view-request
// handoff, because the ticket says the routes may stay reachable by URL. What
// changed is that NOTHING OFFERS THEM: the flag in src/tier.ts is passed to
// AppShell (no Agent Chat nav entry) and to the Dashboards page (no "Request a
// view", no "Compose"), and the unknown-page sentence below stops naming a page
// this tier does not sell. One flag, one file, one edit to undo.
//
// TWO OF THOSE ROUTES ARE NOT ORG-SCOPED, and that is load-bearing rather than
// tidy: `/signin` and `/auth/device` return a null org, so withRouteContext below
// cannot admit "signin" into the org switcher as a membership the reader does not
// have. The sign-in page is also rendered OUTSIDE AppShell — the shell's own
// unauthenticated face is a prompt with no chrome at all (Forge q1: an org list is
// itself a disclosure), and a form is what that prompt now leads to.
//
// The session is READ, never invented — see src/session.ts.
import { useEffect, useState } from 'react';

import { AppShell, activeRole, type SessionPayload } from './shell/AppShell';
import { createAdminClient } from './api/admin';
import { createAuthClient } from './api/auth';
import { createKbsClient } from './api/kbs';
import { createAppearanceClient, type OrgAppearance } from './api/appearance';
import { createOrgsClient } from './api/orgs';
import { createReportsClient } from './api/reports';
import { createDashboardsClient, type RequestViewHandoff } from './api/dashboards';
import { createFilesClient } from './api/files';
import { createInboxClient } from './api/inbox';
import { AdminRoute } from './pages/AdminRoute';
import { BillingPage } from './pages/Billing';
import { DashboardsPage } from './pages/Dashboards';
import { DashboardView } from './pages/DashboardView';
import { DeviceApprovalPage } from './pages/DeviceApproval';
import { HomePage } from './pages/Home';
import { InboxPage } from './pages/Inbox';
import { AppearancePage } from './pages/Appearance';
import { ReportsPage } from './pages/Reports';
import { KbFiles } from './pages/KbFiles';
import { KbWorkspace } from './pages/KbWorkspace';
import { DashboardSwitcher } from './components/DashboardSwitcher';
import { SignInPage } from './pages/SignIn';
import { loadSession, readBootstrap } from './session';
import { UNAUTHENTICATED_EVENT } from './api/transport';
import type { ResolvedTheme, ThemeMode } from './theme-mode';
import { SHOW_AGENT_SURFACES } from './tier';
import { applyAppearanceToRoot } from './appearance-tokens';

/** ONE transport for the life of the app, so a page's load effect is driven by
 *  the route and not by a fresh client object on every render. */
const dashboardsClient = createDashboardsClient();
const adminClient = createAdminClient();
const authClient = createAuthClient();
// E23-t2: the Billing page reads the SAME registry-and-usage surface Home does
// (src/api/kbs.ts), through a container-held client for the same render-loop
// reason as the three above. Home keeps its own default-prop client — that page
// is rendered with no client prop by design and its tests pin that shape.
const kbsClient = createKbsClient();
// E22-t2 (slice 4): the Reports page reads lists and lands consent verbs.
const reportsClient = createReportsClient();
const appearanceClient = createAppearanceClient();
const orgsClient = createOrgsClient();
// E37 PR10: the inbox — the page reads the list; the container reads it once
// per session too, because the nav badge must say "3" before the page is ever
// visited. Same client, same door, two consumers.
const inboxClient = createInboxClient();
// E37-t6: the corpus snapshot — the file browser reads the tree and one
// document at a time; the workspace's Files panel reads the same tree for its
// header facts. One client, one door, two consumers.
const filesClient = createFilesClient();

export type RouteName =
  | 'home'
  | 'inbox'
  | 'kb'
  | 'dashboards'
  | 'dashboard'
  | 'files'
  | 'agent'
  | 'admin'
  | 'billing'
  | 'reports'
  | 'appearance'
  | 'signin'
  | 'device'
  | 'unknown';

export interface Route {
  name: RouteName;
  org: string | null;
  kb: string | null;
  /** the kb_record map key — the per-format dashboard route only */
  format?: string;
  /** /signin — where to go once there is a session. Already validated. */
  next?: string | null;
  /** /auth/device — the `?user_code=` the CLI's link may carry (RFC 8628 §3.3.1) */
  userCode?: string | null;
  /** /:org/:kb/files — the `?doc=` a shared link opens on (E37-t6) */
  doc?: string | null;
}

/**
 * A redirect target this app will actually follow: a path ON THIS ORIGIN.
 *
 * `?next=` is attacker-controllable by construction — it is in a url anybody can
 * send — so anything that is not a single-slash-rooted path is dropped. That
 * excludes `//evil.example` and `/\evil.example`, which browsers resolve as
 * protocol-relative hosts, and every absolute url. Dropping it is silent because
 * there is nothing for the reader to fix: they land on their own home instead.
 */
export function safeNext(next: string | null | undefined): string | null {
  if (typeof next !== 'string' || next === '') return null;
  if (!next.startsWith('/')) return null;
  if (next.startsWith('//') || next.startsWith('/\\')) return null;
  return next;
}

/** Parse one of the app's routes out of a pathname (and, for two of them, the
 *  query string the url carries with it). */
export function parseRoute(pathname: string, search = ''): Route {
  const parts = pathname.split('/').filter(Boolean).map(decodeURIComponent);
  const query = new URLSearchParams(search);
  // E14-t2: the root. NOT org-scoped — Home reads the session's orgs and picks
  // ONE itself (Home.md f2), so the url carries no org and the page is not a
  // directory of everything the reader belongs to.
  if (parts.length === 0) {
    return { name: 'home', org: null, kb: null };
  }
  // E37 PR10: the inbox. NOT org-scoped, like `/` — the list spans every org
  // the session belongs to (the door fans out per membership handle), so the
  // url carries no org. An ORGANIZATION literally named `inbox` is shadowed at
  // its one-segment url the way a KB named `settings` is at its two-segment
  // one; its KBs stay reachable at their own /:org/:kb routes.
  if (parts.length === 1 && parts[0] === 'inbox') {
    return { name: 'inbox', org: null, kb: null };
  }
  // E12-t29: the one anonymous page. No org, no KB — nothing about it is scoped.
  if (parts.length === 1 && parts[0] === 'signin') {
    return { name: 'signin', org: null, kb: null, next: safeNext(query.get('next')) };
  }
  // E12-t30: git-host/src/auth.ts VERIFICATION_PATH, verbatim.
  if (parts.length === 2 && parts[0] === 'auth' && parts[1] === 'device') {
    return { name: 'device', org: null, kb: null, userCode: query.get('user_code') };
  }
  if (parts.length === 3 && parts[2] === 'dashboards') {
    return { name: 'dashboards', org: parts[0], kb: parts[1] };
  }
  // /:org/:kb/dashboards/:format — ONE format's published dashboard, read under
  // the shell. It does not collide with the Worker's own stable url: that one is
  // SINGULAR (/:org/:kb/dashboard/:format, git-host/src/serve.ts) and vite
  // proxies it straight through. This is the app's page; that is the artifact.
  if (parts.length === 4 && parts[2] === 'dashboards') {
    return { name: 'dashboard', org: parts[0], kb: parts[1], format: parts[3] };
  }
  if (parts.length === 3 && parts[2] === 'agent') {
    return { name: 'agent', org: parts[0], kb: parts[1] };
  }
  // E37-t6 — /:org/:kb/files: the corpus snapshot as a readable tree. `?doc=`
  // carries the open document so a view is shareable; it is validated by the
  // page against the manifest, never trusted as a fetch target on its own.
  if (parts.length === 3 && parts[2] === 'files') {
    return { name: 'files', org: parts[0], kb: parts[1], doc: query.get('doc') };
  }
  if (parts.length === 2 && parts[1] === 'settings') {
    return { name: 'admin', org: parts[0], kb: null };
  }
  // E23-t2 (first slice) — vault/Apps/Forge/Pages/Billing.md. Org-scoped like
  // `/:org/settings`, checked before `/:org/:kb` for the same reason and with
  // the SAME shadow: a knowledge base literally named `billing` cannot be
  // reached at its short url, and src/pages/KnowledgeBases.tsx's long-route
  // fallback is what keeps it reachable at all — the list of shadowed names is
  // that file's SHADOWED_KB_NAMES, and this route must stay in step with it.
  if (parts.length === 2 && parts[1] === 'billing') {
    return { name: 'billing', org: parts[0], kb: null };
  }
  // E22-t2 (slice 4) — vault/Apps/Forge/Pages/Reports.md. Same org-scoped
  // shape, same SHADOWED_KB_NAMES obligation as settings and billing.
  if (parts.length === 2 && parts[1] === 'reports') {
    return { name: 'reports', org: parts[0], kb: null };
  }
  // Appearance (Pages/Appearance.md, DocujointOrg.dc.html) — org theme
  // defaults. Same org-scoped shape, same SHADOWED_KB_NAMES obligation.
  if (parts.length === 2 && parts[1] === 'appearance') {
    return { name: 'appearance', org: parts[0], kb: null };
  }
  // E14-t4 — /:org/:kb, THE KNOWLEDGE BASE ITSELF. It is checked LAST of the
  // two-segment routes on purpose: `/:org/settings` is the Admin page and
  // web/test/component/app-routes.test.tsx pins that, so an organization that
  // holds a knowledge base literally named `settings` (git-host/src/auth.ts
  // KB_NAME permits it) cannot reach it at this short url. That collision is
  // real and is not hidden — src/pages/KnowledgeBases.tsx sends that one card to
  // `/:org/:kb/dashboards` instead, which is unambiguous, so the KB is reachable
  // even though its shortest address is taken.
  if (parts.length === 2) {
    return { name: 'kb', org: parts[0], kb: parts[1] };
  }
  return { name: 'unknown', org: parts[0] ?? null, kb: null };
}

/**
 * The session, with the url's own (org, kb) as the ACTIVE pair — and present in
 * the switchers, so what the reader is looking at is what the switcher shows. A
 * role is never added: `orgs` carries roles and this only ever appends an org
 * the caller is demonstrably reading, at the LEAST privilege the shell knows.
 */
function withRouteContext(
  session: SessionPayload,
  org: string | null,
  kb: string | null,
): SessionPayload {
  const orgs = session.orgs.some((candidate) => candidate.name === org)
    ? session.orgs
    : org
      ? [...session.orgs, { id: org, name: org, role: 'viewer' as const }]
      : session.orgs;
  const kbs =
    kb && org && !session.kbs.some((candidate) => candidate.name === kb && candidate.org === org)
      ? [...session.kbs, { id: `${org}/${kb}`, name: kb, org }]
      : session.kbs;
  return { ...session, orgs, kbs, activeOrg: org, activeKb: kb };
}

/** Move the browser to a url and re-render, without a full page load. */
function navigate(href: string) {
  window.history.pushState({}, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * Where a person lands once they have a session, when nothing sent them anywhere
 * in particular. HOME — the page whose whole job is to answer "whose account is
 * this, what is in it, and is anything wrong".
 *
 * E14-t2 CHANGED THIS, AND THE CONTRACT THAT PINNED IT, DELIBERATELY.
 *
 * It used to guess a destination: the first KB the registry happened to list, or
 * failing that the org's SETTINGS page. That was not a preference, it was a
 * workaround — `/` parsed to `unknown`, the app's own root answered "there is no
 * such page", so sign-in had to be pointed at some other surface and the least
 * bad one was picked. Both guesses were wrong in the ways that matter:
 *
 *   · `session.kbs[0]` is an ARBITRARY member of an unordered list. Two people
 *     in the same org could land in different knowledge bases, and one person
 *     could land somewhere new because a colleague created a KB that sorted
 *     first. A landing page must not depend on that.
 *   · Settings is an ADMINISTRATIVE surface. Sending a brand-new account — the
 *     one case guaranteed to have no KBs, since sign-up grants a personal org
 *     and nothing else (git-host/src/login.ts) — straight into org settings
 *     answers a question nobody asked on their first visit.
 *
 * Home now exists and answers both cases properly: it selects ONE organization
 * (E14 q1: last used, else the first), lists its knowledge bases with their
 * referee verdicts, and shows an empty organization an empty state that reads as
 * new rather than broken. So the destination is simply `/`, and the branching is
 * gone rather than relocated.
 *
 * `session` is still the parameter because the caller has it and the signature is
 * what web/test/component/app-routes.test.tsx pins; the value is deliberately
 * unused, which is the honest shape of "this no longer depends on what you have".
 */
export function homeFor(_session: SessionPayload): string {
  return '/';
}

/** The url a reader was on, as a `next` — so signing in returns them to it. */
function signInHref(): string {
  const here = `${window.location.pathname}${window.location.search}`;
  return `/signin?next=${encodeURIComponent(here)}`;
}

export interface AppProps {
  /** E15 q1 — the theme mode and its cycler, owned by the root beside `<Theme>`
   *  (src/main.tsx) and passed straight through to the chrome. The container
   *  holds no theme state of its own; it is a wire, not a decision. */
  themeMode?: ThemeMode;
  onSelectThemeMode?: (mode: ThemeMode) => void;
  /** the mode RESOLVED to the artifact's two-value vocabulary (system already
   *  answered by the OS) — the root owns the resolution (src/main.tsx); this
   *  container only wires it to the framed dashboard so an open frame follows
   *  a theme change without waiting for its next load. */
  resolvedTheme?: ResolvedTheme;
}

export function App({ themeMode, resolvedTheme, onSelectThemeMode }: AppProps = {}) {
  const [route, setRoute] = useState<Route>(() =>
    parseRoute(window.location.pathname, window.location.search),
  );
  const [session, setSession] = useState<SessionPayload | null>(() => readBootstrap());
  // Dashboards f3 → Agent Chat f3: a view request is HANDED OFF, so it is held
  // here (the container outlives the navigation) and carried to the agent page.
  // E08-t3 owns what the agent surface does with it; nothing is landed on the
  // way, because the draft is the agent's and the acceptance is a maintainer's.
  const [viewRequest, setViewRequest] = useState<RequestViewHandoff | null>(null);
  // Has the identity read ANSWERED yet? "No session" and "not asked yet" are the
  // same value (null) and the device page cannot treat them alike: asking a person
  // who is already signed in to type their password, for the half-second the probe
  // takes, is how a login page teaches somebody to type a password at a prompt
  // they did not expect. A bootstrap means the question is already answered.
  const [probed, setProbed] = useState<boolean>(() => readBootstrap() !== null);
  // E14-t2: the ONE org Home settled on. The url for `/` carries no org (Home
  // picks it from the session, remembering the last one used), so this is the
  // only way the shell's switchers can agree with the page under them. It is
  // held here rather than in Home because the shell outlives the page.
  const [homeOrg, setHomeOrg] = useState<string | null>(null);
  // Appearance (Pages/Appearance.md) — the ACTIVE org's saved theme defaults,
  // loaded once per org and painted onto the document root (the effect below).
  // The Appearance page hands a fresh save up through onAppearanceSaved, so a
  // save repaints without a refetch.
  const [orgAppearance, setOrgAppearance] = useState<OrgAppearance | null>(null);
  // The CURRENT dashboard's resolved title (the switcher reads the manifest
  // and hands it up) — the brand menu names it on mobile, where the bar hides
  // the switcher (2026-08-07 verdict).
  const [dashboardTitle, setDashboardTitle] = useState<string | null>(null);
  // E37 PR10 — the inbox's unread count, worn as a badge on the nav entry.
  // null until the door has answered (an unfetched count is not zero); the
  // Inbox page hands fresher numbers up as marks land.
  const [inboxUnread, setInboxUnread] = useState<number | null>(null);

  useEffect(() => {
    const onPop = () => setRoute(parseRoute(window.location.pathname, window.location.search));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // THE SESSION DIED UNDER US (api/transport.ts): any api answer of 401 means
  // the cookie is gone or dead — expired Max-Age under a suspended tab was the
  // first sighting — and the shell must flip to the sign-in prompt rather than
  // let every panel paint refusal cards under signed-in-looking chrome. probed
  // stays true: the 401 IS the answer to "who am I".
  useEffect(() => {
    const onUnauthenticated = () => {
      setSession(null);
      setProbed(true);
    };
    window.addEventListener(UNAUTHENTICATED_EVENT, onUnauthenticated);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, onUnauthenticated);
  }, []);

  // WHO IS ASKING, asked of the control plane once. A server-inlined bootstrap
  // wins when there is one (it costs no round trip); otherwise GET /api/session
  // answers, and 401 leaves the session null — which is the sign-in shell and
  // no chrome at all (Forge q1).
  useEffect(() => {
    if (session !== null) return;
    let live = true;
    void loadSession().then((found) => {
      if (!live) return;
      if (found) setSession(found);
      setProbed(true);
    });
    return () => {
      live = false;
    };
    // one identity read per mount: re-running it on every session change would
    // re-ask the moment it succeeded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // WHOSE LOOK: the active org's appearance, read when the org (or session)
  // changes. A 403/failed read means stock look — the chrome must render for
  // a member whose org read hiccups, so the miss is silent by design here
  // (the Appearance PAGE is where the refusal is shown in words).
  const appearanceOrg =
    session === null ? null : ((route.name === 'home' ? homeOrg : route.org) ?? session.activeOrg);
  useEffect(() => {
    if (session === null || !appearanceOrg) {
      setOrgAppearance(null);
      return;
    }
    let live = true;
    void appearanceClient
      .load(appearanceOrg)
      .then((appearance) => {
        if (live) setOrgAppearance(appearance);
      })
      .catch(() => {
        if (live) setOrgAppearance(null);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session === null, appearanceOrg]);

  // The PAINT: the saved choice's vars land on the document root, and are
  // CLEARED when there is nothing saved — stock is absence, not a copy.
  useEffect(() => {
    applyAppearanceToRoot(document.documentElement, orgAppearance);
    return () => applyAppearanceToRoot(document.documentElement, null);
  }, [orgAppearance]);

  // E37 PR10 — the badge's number, read once per identity: the rail should
  // say "3" before the inbox is ever opened. A failed read costs the badge
  // alone (null = the entry unadorned), never the chrome — the page itself is
  // where a refusal is shown in words.
  useEffect(() => {
    if (session === null) {
      setInboxUnread(null);
      return;
    }
    let live = true;
    void inboxClient
      .list()
      .then((answer) => {
        if (live) setInboxUnread(answer.unread);
      })
      .catch(() => {
        if (live) setInboxUnread(null);
      });
    return () => {
      live = false;
    };
    // once per identity: the Inbox page hands fresher counts up as marks land
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session === null]);

  // Keep the shell's active context in step with the url the reader is on: the
  // url is the source of truth, so a shared link opens on the right KB.
  //
  // The route's own org and KB are ADMITTED to the switchers when the control
  // plane's lists do not already carry them. That is not an invented membership
  // — the page the reader is on was served to them by a members-only Worker, and
  // a KB that is not in the `kbs` registry (nothing writes that table yet for a
  // pushed repository) would otherwise leave the switcher showing nothing while
  // the reader is plainly looking at it.
  //
  // `/` is the exception that proves it: that url names no org at all, so the
  // ACTIVE org there is the one Home chose (E14-t2), handed up through
  // `onOrgSelected`. Every value it can carry came out of `session.orgs`, so
  // nothing is admitted to the switchers that the control plane did not list.
  const contextOrg = route.name === 'home' ? homeOrg : route.org;
  const active: SessionPayload | null =
    session === null
      ? null
      : withRouteContext(session, contextOrg ?? session.activeOrg, route.kb ?? session.activeKb);

  /** The identity a fresh sign-in hands up, and the one place it lands. */
  const signedIn = (found: SessionPayload, next: string | null) => {
    setSession(found);
    navigate(next ?? homeFor(found));
  };

  // THE ONE ANONYMOUS PAGE, rendered outside the shell — see the header. It is
  // also what an unauthenticated caller gets on /auth/device: approving a code
  // needs a session, and sending them here with a `next` is what brings them back
  // to the code they were holding instead of dropping it.
  if (route.name === 'signin') {
    return <SignInPage client={authClient} onSignedIn={(found) => signedIn(found, route.next ?? null)} />;
  }
  if (route.name === 'device' && session === null && !probed) {
    return <p data-page="device">Checking your session…</p>;
  }
  // E14-t2, for the same reason and one step earlier: AppShell's null-session
  // face is the members-only PROMPT, and showing it while GET /api/session is
  // still in flight tells somebody who is signed in that they are not. Home
  // renders its own "still asking" state, so it is rendered outside the shell
  // for exactly as long as the question is open.
  if (route.name === 'home' && session === null && !probed) {
    return <HomePage session={null} probed={false} />;
  }
  if (route.name === 'device' && session === null) {
    return (
      <SignInPage
        client={authClient}
        note="Sign in first: approving a terminal signs YOUR identity into it, so the forge has to know who you are."
        onSignedIn={(found) => signedIn(found, `${window.location.pathname}${window.location.search}`)}
      />
    );
  }
  // …and the SAME rule for every other route: AppShell's null-session face is
  // the members-only prompt, and rendering it while GET /api/session is still
  // in flight flashes "you are not signed in" at every signed-in person on
  // every fresh document load (reported 2026-08-06, one screenshot per page).
  // While the question is open the page says it is asking, and nothing else —
  // no chrome, no claim.
  if (session === null && !probed) {
    return <p data-page="probing">Checking your session…</p>;
  }

  // E25 — WHICH ALTITUDE the shell draws: the dashboard view is full-bleed
  // with the brand menu; every KB-scoped route is the KB's workspace; all
  // else is the organization's.
  const altitude: 'org' | 'kb' | 'dashboard' =
    route.name === 'dashboard'
      ? 'dashboard'
      : route.kb !== null
        ? 'kb'
        : 'org';

  return (
    <AppShell
      session={active}
      altitude={altitude}
      topExtra={
        altitude === 'dashboard' && route.org && route.kb && route.format ? (
          <DashboardSwitcher
            org={route.org}
            kb={route.kb}
            format={route.format}
            client={dashboardsClient}
            onNavigate={navigate}
            onTitle={(title) =>
              setDashboardTitle((current) => (current === title ? current : title))
            }
          />
        ) : undefined
      }
      dashboardLabel={
        altitude === 'dashboard' ? (dashboardTitle ?? route.format) : undefined
      }
      // E14-t5 — THE TIER GATE, applied where the product is composed rather
      // than inside the shell. src/tier.ts is the one file that decides.
      showAgentSurfaces={SHOW_AGENT_SURFACES}
      // E37 PR10 — the inbox badge's number; the shell only wears it.
      inboxUnread={inboxUnread ?? undefined}
      themeMode={themeMode}
      onSelectThemeMode={onSelectThemeMode}
      // Create an organization (2026-08-07): the store lands it with the
      // caller as its first admin; the session is re-read (the org list is
      // the control plane's to state) and the app lands on the new org's
      // overview. A refusal propagates into the drawer's own form.
      onCreateOrg={async (name) => {
        await orgsClient.createOrg(name);
        const found = await loadSession();
        if (found) setSession(found);
        setHomeOrg(name);
        navigate('/');
      }}
      // E21-t2 — the rail marks the entry the reader is ON. The pathname is the
      // route's own source of truth (parseRoute reads it), re-read on every
      // route change because `route` state changes re-render this component.
      currentPath={window.location.pathname}
      // E21 follow-up (2026-08-06): rail clicks route IN-APP. Before this, each
      // entry's <a href> was a full document load — a re-boot that re-asked
      // /api/session and flashed the members-only prompt on every navigation.
      onNavigate={navigate}
      // E14-t6 — THE ONE ORGANIZATION CONTROL IN THE PRODUCT. Home used to
      // carry a second one; the shell's is now the only one, so this callback
      // has to work on `/` as well as on an org-scoped url.
      //
      // The choice is ALWAYS recorded (`homeOrg`), because that is what Home
      // reads and what the shell's own context is computed from on `/`. What
      // differs is the navigation: on Home there is none — the page under the
      // chrome re-renders on the organization just chosen, which is the whole
      // point of collapsing the two controls — while every other surface is
      // (org, kb)-scoped and cannot survive its org changing under it, so it
      // lands where E08-t1 sent it: the one org-scoped page that needs no KB.
      onSelectOrg={(org) => {
        setHomeOrg((current) => (current === org ? current : org));
        if (route.name !== 'home') navigate(`/${org}/settings`);
      }}
      onSelectKb={(kb) => active?.activeOrg && navigate(`/${active.activeOrg}/${kb}/dashboards`)}
      // The shell's prompt no longer hopes for a server-inlined bootstrap: there
      // is a form now, and it is a page (E12-t29).
      onSignIn={() => navigate(signInHref())}
      // Tenancy and Guards s4, on the one channel a browser has. The row is
      // revoked server-side and the cookie expired; the app forgets the identity
      // either way, because a client that keeps showing chrome for a session the
      // Worker has cut is lying to the person reading it.
      onSignOut={() => {
        void authClient.signOut().finally(() => {
          setSession(null);
          navigate('/signin');
        });
      }}
    >
      <PageOutlet
        route={route}
        session={active}
        probed={probed}
        // The org the CHROME is on, handed down so Home shows what the shell's
        // switcher says (E14-t6). It is the same value Home hands up, which is
        // why the page ignores one that names no org it can reach.
        homeOrg={homeOrg}
        viewRequest={viewRequest}
        onRequestView={(handoff) => {
          setViewRequest(handoff);
          navigate(`/${handoff.org}/${handoff.kb}/agent`);
        }}
        // The org Home settled on. Stored only when it CHANGES, so a container
        // that re-renders on every keystroke of state cannot turn this into a
        // render loop with the page's own effect.
        onHomeOrg={(orgName) =>
          setHomeOrg((current) => (current === orgName ? current : orgName))
        }
        onAppearanceSaved={setOrgAppearance}
        onInboxUnread={setInboxUnread}
        artifactTheme={resolvedTheme}
      />
    </AppShell>
  );
}

/** The page outlet. E08-t3 (Agent Chat) is the one page still unbuilt; until it
 *  lands the shell says what it is showing rather than rendering an empty frame. */
function PageOutlet({
  route,
  session,
  probed,
  homeOrg,
  viewRequest,
  onRequestView,
  onHomeOrg,
  onAppearanceSaved,
  onInboxUnread,
  artifactTheme,
}: {
  route: Route;
  session: SessionPayload | null;
  probed: boolean;
  homeOrg: string | null;
  viewRequest: RequestViewHandoff | null;
  onRequestView: (handoff: RequestViewHandoff) => void;
  onHomeOrg: (orgName: string) => void;
  onAppearanceSaved: (appearance: OrgAppearance) => void;
  /** E37 PR10 — the Inbox page hands fresh unread counts up for the badge */
  onInboxUnread: (unread: number) => void;
  /** src/main.tsx's resolved theme, riding to the framed dashboard alone */
  artifactTheme?: ResolvedTheme;
}) {
  // E14-t5: this sentence NAMED Agent Chat, which is offering it — the one place
  // outside the nav where the app advertised a surface this tier does not sell.
  // The page list follows the flag rather than being frozen, so re-enabling the
  // agent does not leave the 404 lying about what the forge ships.
  if (route.name === 'unknown') {
    return (
      <p>
        There is no such page. The forge ships Home, Inbox, Dashboards
        {SHOW_AGENT_SURFACES ? ', Agent Chat' : ''} and Admin.
      </p>
    );
  }

  // E14-t2 — vault/Apps/Forge/Pages/Home.md. ONE organization, named, with the
  // reader's role in it; the page chooses which one (last used, else the first)
  // and hands it up. A signed-out caller never reaches this: AppShell's own
  // unauthenticated face is the members-only prompt (Forge q1).
  if (route.name === 'home') {
    return (
      <HomePage
        session={session}
        probed={probed}
        activeOrg={homeOrg}
        onOrgSelected={onHomeOrg}
      />
    );
  }

  // E37 PR10 — the inbox: where you were mentioned, across every KB. Session-
  // scoped like Home (the door fans out per membership), rendered under the
  // shell like every members-only page; a fresh unread count is handed up so
  // the rail's badge is the page's own number.
  if (route.name === 'inbox') {
    return <InboxPage client={inboxClient} onUnread={onInboxUnread} />;
  }

  // E25-t2 — /:org/:kb IS THE KNOWLEDGE BASE'S WORKSPACE now. This supersedes
  // E14-t4's open-straight-into-the-dashboard journey by the epic's verdict;
  // the dashboard is one click away through the workspace's own panel, and
  // everything else the KB holds finally has a room.
  if (route.name === 'kb' && route.org && route.kb) {
    return (
      <KbWorkspace
        org={route.org}
        kb={route.kb}
        dashboards={dashboardsClient}
        reports={reportsClient}
        kbs={kbsClient}
        files={filesClient}
        showAgentSurfaces={SHOW_AGENT_SURFACES}
      />
    );
  }

  // E37-t6 — /:org/:kb/files: the vault as a collapsible tree, one document
  // rendered as markdown at a time. KB-scoped like the workspace it is
  // entered from; the Worker's refusals are what the page relays.
  if (route.name === 'files' && route.org && route.kb) {
    return (
      <KbFiles org={route.org} kb={route.kb} files={filesClient} initialDoc={route.doc ?? null} />
    );
  }

  if (route.name === 'dashboards' && route.org && route.kb) {
    // The role is the one the control plane holds for this person in THIS org
    // (git-host/src/serve.ts resolveActor). When the session cannot say, the
    // page is drawn at the LEAST privilege — the write surface is offered only
    // to a role that is known to hold it, and the Worker refuses regardless.
    const role = (session && activeRole(session)) ?? 'viewer';
    return (
      <DashboardsPage
        org={route.org}
        kb={route.kb}
        role={role}
        client={dashboardsClient}
        onRequestView={onRequestView}
        // E14-t5: no "Request a view", no "Compose". src/tier.ts.
        showAgentSurfaces={SHOW_AGENT_SURFACES}
      />
    );
  }

  // Dashboards f2: the published pair, served from R2 and read under the shell.
  // Nothing is re-rendered here — see src/pages/DashboardView.tsx.
  if (route.name === 'dashboard' && route.org && route.kb && route.format) {
    return (
      <DashboardView
        org={route.org}
        kb={route.kb}
        format={route.format}
        artifactTheme={artifactTheme}
      />
    );
  }

  // E23-t2 (first slice) — vault/Apps/Forge/Pages/Billing.md. Org-scoped, no KB.
  // Rendered for whoever reaches the url — the nav offers it to the active org's
  // admin alone (AppShell navEntriesFor), and the Worker's refusals are what the
  // page relays: hiding is honesty, the guard is the enforcement.
  if (route.name === 'billing' && route.org) {
    return <BillingPage org={route.org} client={kbsClient} />;
  }

  // E22-t2 (slice 4) — vault/Apps/Forge/Pages/Reports.md. Org-scoped, every
  // member may READ (the Worker's generic session-and-read guard answers the
  // list); the consent buttons are drawn for govern alone, and the Worker
  // refuses regardless.
  if (route.name === 'reports' && route.org) {
    const role = (session && activeRole(session)) ?? 'viewer';
    return <ReportsPage org={route.org} role={role} kbs={kbsClient} reports={reportsClient} />;
  }

  // Appearance (Pages/Appearance.md) — org-scoped, no KB. Every member READS
  // the org's look (it is the chrome they see); the save is govern's, and the
  // page draws the buttons for that role alone while the Worker refuses
  // regardless.
  if (route.name === 'appearance' && route.org) {
    const role = session ? activeRole(session) : null;
    return (
      <AppearancePage
        org={route.org}
        role={role}
        client={appearanceClient}
        onSaved={onAppearanceSaved}
      />
    );
  }

  // E08-t4: the Admin page, MOUNTED. It was written, tested and never imported —
  // `/:org/settings` fell through to the empty paragraph below, and admin.test.tsx
  // renders the component directly, which is why nothing caught it. What the
  // container may honestly feed it is src/pages/AdminRoute.tsx's whole subject.
  if (route.name === 'admin' && route.org && session) {
    return <AdminRoute org={route.org} session={session} client={adminClient} />;
  }

  // E12-t30: the human half of the device-code login. A session is guaranteed
  // here — App sends an anonymous caller to /signin with a `next` first — and it
  // matters, because approving attaches THAT identity to the terminal.
  if (route.name === 'device' && session) {
    return (
      <DeviceApprovalPage
        client={authClient}
        userCode={route.userCode ?? null}
        actorEmail={session.user.email}
      />
    );
  }

  // Reachable by URL and by nothing else since E14-t5: with the tier gate closed
  // no surface hands a view request off, so this branch is now only what a
  // re-enabled tier walks through. It is kept rather than deleted for exactly
  // that reason — hiding is not retiring.
  if (route.name === 'agent' && viewRequest) {
    return (
      <p data-page={route.name}>
        A view request for “{viewRequest.format}” is waiting to be sent to the agent.
      </p>
    );
  }

  return <p data-page={route.name} />;
}
